'use client';

import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { BubbleRows } from '@/components/chat/BubbleBlock';
import { quantityMark, type JobBriefBlock } from '@/lib/chat/blocks/job-brief';

/**
 * The job brief inside a chat reply (`job-brief.ts`): the tiles, the property's
 * notes, what was quoted, earlier visits behind a toggle, and Open the job.
 *
 * Every word and figure comes off the block, which the server built from its
 * own read; nothing here works anything out. There is nothing to send or save,
 * so nothing is orange: the only taps go somewhere (a chevron row) or open the
 * earlier visits in place.
 *
 * `live` is accepted and deliberately not used to switch taps off. Every tap
 * here opens a page or a list, the same as the week timeline's stops, and a
 * job's address does not go stale: opening it from a reply a month old is
 * still right. A component that answers or writes is what `live` is for.
 */
export interface JobBriefProps {
  block: JobBriefBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}

export function JobBrief({ block, onOpen }: JobBriefProps) {
  const [open, setOpen] = useState(false);
  const visitsId = useId();
  const sub = [block.when, block.where].filter(Boolean).join(' · ');
  const visitCount = block.visits.length + block.visitsMore;
  const quoteHeading = block.quote ? `From the ${block.quote.noun}${block.quote.reference ? ` ${block.quote.reference}` : ''}` : null;

  return (
    <div className="mchat-brief" role="group" aria-label={`Job brief: ${block.title}`}>
      <div className="mchat-brief-head">
        <span className="mchat-brief-title">{block.title}</span>
        {sub && <span className="mchat-brief-sub">{sub}</span>}
      </div>

      {block.tiles.length > 0 && (
        <dl className="mchat-brief-tiles">
          {block.tiles.map(tile => (
            <div className={`mchat-brief-tile mchat-brief-tile--${tile.kind}`} key={tile.kind}>
              <dt className="mchat-brief-label">{tile.label}</dt>
              <dd className="mchat-brief-value">{tile.value}</dd>
              {tile.detail && <dd className="mchat-brief-detail">{tile.detail}</dd>}
            </div>
          ))}
        </dl>
      )}

      {block.notes && (
        <div className="mchat-brief-section">
          <div className="mchat-brief-label">Notes</div>
          {/* Verbatim. Parking and key-safe live here when anyone wrote them
              down; nothing is lifted out of it into a tile. */}
          <p className="mchat-brief-notes">{block.notes}</p>
        </div>
      )}

      {block.quote && (
        <div className="mchat-brief-section">
          <div className="mchat-brief-label">{quoteHeading}</div>
          <ul className="mchat-brief-lines">
            {block.quote.lines.map((line, i) => {
              const mark = quantityMark(line.quantity);
              return (
                <li className="mchat-brief-line" key={`${i}:${line.description}`}>
                  <span className="mchat-brief-line-desc">{line.description}</span>
                  {mark && <span className="mchat-brief-line-qty">{mark}</span>}
                </li>
              );
            })}
          </ul>
          {block.quote.more > 0 && (
            <p className="mchat-brief-more">{block.quote.more} more line{block.quote.more === 1 ? '' : 's'} on the job</p>
          )}
        </div>
      )}

      {visitCount > 0 && (
        <div className="mchat-brief-visits">
          <button
            type="button"
            className="mchat-brief-toggle"
            aria-expanded={open}
            aria-controls={visitsId}
            onClick={() => setOpen(o => !o)}
          >
            <span className="mchat-brief-toggle-text">Earlier visits ({visitCount})</span>
            <Icon icon={ChevronDown} size={16} className={`mchat-brief-toggle-icon${open ? ' mchat-brief-toggle-icon--open' : ''}`} />
          </button>
          {open && (
            <div id={visitsId}>
              <BubbleRows
                label={`${block.visits.length} earlier visits`}
                rows={block.visits.map(v => ({
                  id: v.jobId,
                  title: v.what,
                  meta: v.when ?? undefined,
                  kind: 'go' as const,
                  onPress: () => onOpen(v.path),
                }))}
              />
              {block.visitsMore > 0 && (
                <p className="mchat-brief-more">{block.visitsMore} earlier not shown</p>
              )}
            </div>
          )}
        </div>
      )}

      <BubbleRows rows={[{ id: 'open-job', title: 'Open the job', kind: 'go', onPress: () => onOpen(block.path) }]} />
    </div>
  );
}
